import { useEffect, useState } from "react";
import { X, LayoutDashboard, Target, TrendingUp, Gauge, Eye, EyeOff, Link2, Loader2, Save, Check, ShieldCheck, BarChart3, Flame, Car, Landmark } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// Same metadata as the global visibility card so both surfaces feel like
// one system. Keep in sync when adding new dashboards.
const DASHBOARDS = [
  { key: "crm_overview",       label: "CRM Overview",    desc: "Vista global del pipeline de leads.",     icon: <LayoutDashboard size={16}/>, accent: "#111827" },
  { key: "leads_performance",  label: "Lead Performance", desc: "Canales digitales en tiempo real.",       icon: <Target size={16}/>,          accent: "#f4c400" },
  { key: "predictive",         label: "Forecast e Inventario", desc: "Forecast + calculadora de meta + stock + inventario.", icon: <TrendingUp size={16}/>, accent: "#3b82f6" },
  { key: "scorecard",          label: "Scorecard ND",     desc: "KPIs comerciales y comparador de UTM.",   icon: <Gauge size={16}/>,           accent: "#8b5cf6" },
  { key: "zonas_hot",          label: "Zonas Hot SDM",    desc: "Ventas Zonas Hot por sucursal y mes.",   icon: <Flame size={16}/>,           accent: "#ea580c" },
  { key: "trade_in_potential", label: "Trade-In Seminuevos", desc: "Bloque en CRM Overview: vehículo actual e intención de tasación (oculto por defecto).", icon: <Car size={16}/>, accent: "#059669" },
  { key: "sales_performance",  label: "Sales Performance", desc: "Command center comercial (Gerentes).",   icon: <BarChart3 size={16}/>,       accent: "#dc2626" },
  { key: "fni",                label: "F&I · Financiamiento", desc: "Bancos, sometidas y aprobaciones (oculto por defecto).", icon: <Landmark size={16}/>, accent: "#0891b2" },
];

// 3-state toggle: ON, OFF, INHERIT (null → follow global default).
// Rendered as a segmented button so all 3 states are visible at once —
// hides the "why is this on by default?" support question.
const STATES = [
  { value: true,  label: "ON",   Icon: Eye,     bg: "#dcfce7", fg: "#166534" },
  { value: false, label: "OFF",  Icon: EyeOff,  bg: "#fee2e2", fg: "#991b1b" },
  { value: null,  label: "Auto", Icon: Link2,   bg: "#f1f5f9", fg: "#475569" },
];

/**
 * Per-user dashboard access modal. Opens from the users table row.
 * Loads the target user's stored override + global flags + effective
 * flags, lets the admin flip each dashboard to ON / OFF / INHERIT, and
 * saves via PUT `/admin/users/{id}/dashboard-access`.
 *
 * Admin targets are read-only — showing all-True effective flags with a
 * "no se puede restringir" banner. This mirrors the backend guardrail
 * so the UI can't accidentally send a request that would silently be
 * ignored.
 */
export default function UserDashboardAccessModal({ open, onClose, userId, userEmail }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [override, setOverride] = useState({});   // {key: true|false|null}

  useEffect(() => {
    if (!open || !userId) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/admin/users/${userId}/dashboard-access`);
        if (!mounted) return;
        setData(r.data);
        setOverride({ ...(r.data.override || {}) });
      } catch (e) {
        toast.error("No se pudo cargar la config de dashboards",
          { description: e.response?.data?.detail || e.message });
        onClose?.();
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open, userId, onClose]);

  const isAdmin = data?.is_admin;
  const hasRoleLock = !!data?.role_lock;
  const roleLabel = data?.role === "sales_manager" ? "Gerente de Ventas" : data?.role;
  const dirty = data
    ? DASHBOARDS.some((d) => (override[d.key] ?? null) !== (data.override?.[d.key] ?? null))
    : false;

  const setState = (key, value) => {
    setOverride((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      DASHBOARDS.forEach((d) => { payload[d.key] = override[d.key] ?? null; });
      const r = await api.put(`/admin/users/${userId}/dashboard-access`, payload);
      setData(r.data);
      setOverride({ ...(r.data.override || {}) });
      toast.success("Configuración guardada", {
        description: `Actualizados los accesos de ${userEmail}`,
      });
    } catch (e) {
      toast.error("No se pudo guardar",
        { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const resetAllAuto = () => {
    const next = {};
    DASHBOARDS.forEach((d) => { next[d.key] = null; });
    setOverride(next);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 42, 0.75)" }}
      onClick={onClose}
      data-testid="user-dashboard-access-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ maxWidth: 720, maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}>

        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: "#0f172a", color: "#fff" }}>
          <div className="flex items-center gap-3">
            <div className="rounded-lg flex items-center justify-center"
              style={{ width: 40, height: 40, background: "#f4c400", color: "#0f172a" }}>
              <ShieldCheck size={20} strokeWidth={2.4}/>
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-tight">
                Accesos a Dashboards
              </div>
              <div className="text-xs font-semibold opacity-75 mt-0.5 truncate max-w-[420px]"
                title={userEmail}>
                {userEmail}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10"
            data-testid="user-dashboard-access-close">
            <X size={20}/>
          </button>
        </div>

        <div className="p-6 flex-1 overflow-auto">
          {loading && (
            <div className="py-16 flex items-center justify-center gap-2 text-gray-500 font-semibold">
              <Loader2 size={16} className="animate-spin"/> Cargando configuración…
            </div>
          )}

          {!loading && isAdmin && (
            <div className="p-4 rounded-lg flex items-start gap-3"
              style={{ background: "#fef3c7", border: "1px solid #fbbf24" }}>
              <ShieldCheck size={18} style={{ color: "#a16207", flexShrink: 0, marginTop: 2 }}/>
              <div className="text-xs font-semibold text-amber-900" style={{ lineHeight: 1.5 }}>
                <strong>{userEmail}</strong> es <strong>administrador</strong>. Los admins nunca
                pueden ser restringidos — siempre ven todos los dashboards.
                Cambia el rol a &ldquo;viewer&rdquo; primero si quieres controlar sus accesos.
              </div>
            </div>
          )}

          {!loading && !isAdmin && hasRoleLock && data && (
            <div className="p-4 rounded-lg flex items-start gap-3 mb-4"
              style={{ background: "#dbeafe", border: "1px solid #60a5fa" }}>
              <ShieldCheck size={18} style={{ color: "#1d4ed8", flexShrink: 0, marginTop: 2 }}/>
              <div className="text-xs font-semibold text-blue-900" style={{ lineHeight: 1.5 }}>
                <strong>{userEmail}</strong> tiene el rol <strong>{roleLabel}</strong>, que impone
                un conjunto fijo de dashboards. Los accesos efectivos se muestran abajo pero
                no pueden modificarse mientras conserve ese rol. Cambia su rol a
                &ldquo;Viewer&rdquo; para personalizar accesos.
              </div>
            </div>
          )}

          {!loading && !isAdmin && hasRoleLock && data && (
            <div className="space-y-2">
              {DASHBOARDS.map((d) => {
                const effective = data.effective?.[d.key];
                return (
                  <div key={d.key} className="rounded-lg p-3 flex items-center gap-3 flex-wrap"
                    style={{ border: "1px solid #e2e8f0", background: "#f8fafc", opacity: 0.95 }}
                    data-testid={`user-dashboard-row-${d.key}`}>
                    <div className="rounded-lg flex items-center justify-center"
                      style={{ width: 32, height: 32, background: `${d.accent}12`, color: d.accent }}>
                      {d.icon}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-sm">{d.label}</span>
                        <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded uppercase"
                          style={{
                            background: effective ? "#dcfce7" : "#fee2e2",
                            color:      effective ? "#166534" : "#991b1b",
                          }}>
                          {effective ? "Visible" : "Oculto"}
                        </span>
                        <span className="text-[10px] text-blue-700 font-extrabold uppercase tracking-wide">
                          · Fijado por rol
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 font-semibold mt-0.5">{d.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !isAdmin && !hasRoleLock && data && (
            <>
              <div className="mb-4 text-xs text-gray-600 font-semibold" style={{ lineHeight: 1.55 }}>
                Cada dashboard tiene 3 estados:
                <strong className="text-emerald-700"> ON</strong> (siempre visible),
                <strong className="text-red-700"> OFF</strong> (siempre oculto),
                <strong className="text-slate-700"> Auto</strong> (sigue el valor global).
              </div>

              <div className="space-y-2">
                {DASHBOARDS.map((d) => {
                  const value = override[d.key] ?? null;
                  const effective = data.effective?.[d.key];
                  const global = data.global_flags?.[d.key] !== false;
                  return (
                    <div key={d.key} className="rounded-lg p-3 flex items-center gap-3 flex-wrap"
                      style={{ border: "1px solid #e2e8f0", background: "#fff" }}
                      data-testid={`user-dashboard-row-${d.key}`}>
                      <div className="rounded-lg flex items-center justify-center"
                        style={{ width: 32, height: 32, background: `${d.accent}12`, color: d.accent }}>
                        {d.icon}
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-sm">{d.label}</span>
                          <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded uppercase"
                            style={{
                              background: effective ? "#dcfce7" : "#fee2e2",
                              color:      effective ? "#166534" : "#991b1b",
                            }}>
                            {effective ? "Visible" : "Oculto"}
                          </span>
                          {value === null && (
                            <span className="text-[10px] text-gray-400 font-semibold">
                              (global: {global ? "ON" : "OFF"})
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-500 font-semibold mt-0.5">{d.desc}</div>
                      </div>
                      <div className="flex items-center gap-1 rounded-lg p-1"
                        style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        {STATES.map((s) => {
                          const sel = value === s.value;
                          const Ic = s.Icon;
                          return (
                            <button
                              key={String(s.value)}
                              onClick={() => setState(d.key, s.value)}
                              data-testid={`user-dashboard-set-${d.key}-${s.label.toLowerCase()}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-extrabold transition-all"
                              style={{
                                background: sel ? s.bg : "transparent",
                                color:      sel ? s.fg : "#94a3b8",
                              }}
                              title={s.label === "Auto"
                                ? "Hereda del toggle global"
                                : `Forzar ${s.label} solo para este usuario`}>
                              <Ic size={12}/> {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {!loading && !isAdmin && !hasRoleLock && (
          <div className="px-6 py-4 flex items-center justify-between gap-3"
            style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
            <button
              type="button" onClick={resetAllAuto}
              data-testid="user-dashboard-reset"
              className="text-xs font-extrabold text-gray-600 hover:text-gray-900">
              Todo en &ldquo;Auto&rdquo;
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-extrabold text-gray-700 hover:bg-gray-100"
                data-testid="user-dashboard-cancel">
                Cerrar
              </button>
              <button
                onClick={save}
                disabled={!dirty || saving}
                data-testid="user-dashboard-save"
                className="px-5 py-2 rounded-lg text-sm font-extrabold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: dirty && !saving ? "#0f172a" : "#e2e8f0",
                  color:      dirty && !saving ? "#f4c400" : "#94a3b8",
                }}>
                {saving
                  ? <><Loader2 size={14} className="animate-spin"/> Guardando…</>
                  : <><Save size={14}/> Guardar</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
